import { EMU, ENV } from '@/globals/typescript/utils/host'
import { EMULATOR_HOST } from '@/globals/typescript/utils/constants'
import { FIREBASE_WEB_CONFIGS } from '@/firebase/firebaseConfigs'
import { Capacitor } from '@capacitor/core'
import { initializeApp } from 'firebase/app'
import { connectStorageEmulator, getStorage } from 'firebase/storage'
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  connectAuthEmulator,
} from 'firebase/auth'
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  memoryLocalCache,
} from 'firebase/firestore'

// Optional imports – uncomment as needed
// import { getFunctions } from 'firebase/functions'

/**
 * Firebase configuration — the one for THIS build's environment (see `host.ts` for how the build
 * mode picks it). Never a single fixed project: a prod build must talk to prod's Auth, Firestore
 * and Storage, the same project its API calls go to.
 */
const firebaseConfig = FIREBASE_WEB_CONFIGS[ENV.projectID]

/**
 * Initialize Firebase
 */
const app = initializeApp(firebaseConfig)

/**
 * Firebase services
 */
// On native Capacitor builds, getAuth()'s default browserPopupRedirectResolver
// tries to open a gapi-based iframe to apis.google.com, which throws under the
// capacitor://localhost origin and blocks app startup. initializeAuth() with
// explicit persistence skips that resolver entirely.
const auth = Capacitor.isNativePlatform()
  ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
  : getAuth(app)

if (EMU.isEmulatingAuth) {
  connectAuthEmulator(auth, `http://${EMULATOR_HOST}:9099`, { disableWarnings: true })
}

// On native Capacitor builds, Firestore enables disk persistence by default.
// On web, we explicitly enable multi-tab IndexedDB persistence.
// During emulation we use in-memory cache to avoid stale cached data.
const db = initializeFirestore(app, {
  localCache: EMU.isEmulatingFirestore || Capacitor.isNativePlatform()
    ? memoryLocalCache()
    : persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
})

if (EMU.isEmulatingFirestore) {
  connectFirestoreEmulator(db, EMULATOR_HOST, 8080)
}

const storage = getStorage(app)

if (EMU.isEmulatingStorage) {
  connectStorageEmulator(storage, EMULATOR_HOST, 9199)
}

// Optional exports
// const functions = getFunctions(app)

export {
  app,
  auth,
  db,
  storage,
  // functions,
}

